import { useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { ArrowLeft, Copy, KeyRound, QrCode, Smartphone } from "lucide-react";
import { toast } from "sonner";

import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { firstAccessibleRoute } from "../lib/menuAccess";


function apiErrorMessage(err, fallback) {
  if (!err.response) return err.message || fallback;
  return formatApiError(err.response?.data?.detail) || fallback;
}

export default function OtpVerification() {
  const navigate = useNavigate();
  const {
    user,
    pendingTwoFactor,
    verifyTwoFactor,
    loadTwoFactorSetup,
    clearPendingTwoFactor,
  } = useAuth();
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [loadingSetup, setLoadingSetup] = useState(false);
  const [error, setError] = useState("");
  const [now, setNow] = useState(Date.now());
  const loadTwoFactorSetupRef = useRef(loadTwoFactorSetup);

  const setupRequired = Boolean(pendingTwoFactor?.setupRequired);
  const qrCodeDataUri = pendingTwoFactor?.qrCodeDataUri;
  const manualSetupKey = pendingTwoFactor?.manualSetupKey;

  useEffect(() => {
    loadTwoFactorSetupRef.current = loadTwoFactorSetup;
  });

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!setupRequired || qrCodeDataUri || !pendingTwoFactor?.challengeId) return;

    let active = true;
    setLoadingSetup(true);
    loadTwoFactorSetupRef.current()
      .catch((err) => {
        if (active) setError(apiErrorMessage(err, "QR Code authenticator gagal dimuat"));
      })
      .finally(() => {
        if (active) setLoadingSetup(false);
      });
    return () => { active = false; };
  }, [
    pendingTwoFactor?.challengeId,
    qrCodeDataUri,
    setupRequired,
  ]);

  const secondsRemaining = useMemo(
    () => Math.max(0, Math.ceil(((pendingTwoFactor?.expiresAt || 0) - now) / 1000)),
    [pendingTwoFactor?.expiresAt, now]
  );
  const challengeExpired = secondsRemaining === 0;

  if (user) return <Navigate to={firstAccessibleRoute(user)} replace />;
  if (!pendingTwoFactor) return <Navigate to="/login" replace />;

  const onVerify = async (event) => {
    event.preventDefault();
    if (!/^\d{6}$/.test(code)) {
      setError("Masukkan kode authenticator 6 digit");
      return;
    }
    if (challengeExpired) {
      setError("Sesi verifikasi 2FA sudah kedaluwarsa. Silakan login kembali");
      return;
    }

    setVerifying(true);
    setError("");
    try {
      const authenticatedUser = await verifyTwoFactor(code);
      toast.success(setupRequired ? "Authenticator berhasil diaktifkan" : "Kode authenticator berhasil diverifikasi");
      navigate(firstAccessibleRoute(authenticatedUser), { replace: true });
    } catch (err) {
      setError(apiErrorMessage(err, "Kode authenticator gagal diverifikasi"));
    } finally {
      setVerifying(false);
    }
  };

  const copyManualKey = async () => {
    try {
      await navigator.clipboard.writeText(manualSetupKey);
      toast.success("Kunci setup disalin");
    } catch {
      toast.error("Kunci setup tidak dapat disalin otomatis");
    }
  };

  const backToLogin = () => {
    clearPendingTwoFactor();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-6">
      <div className="w-full max-w-lg bg-white border border-zinc-200 p-8 shadow-sm">
        <button
          type="button"
          onClick={backToLogin}
          className="flex items-center gap-1.5 text-xs font-semibold text-zinc-500 hover:text-zinc-900"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Kembali ke login
        </button>

        <div className="mt-8 w-12 h-12 bg-[#002FA7]/10 text-[#002FA7] flex items-center justify-center">
          {setupRequired ? <QrCode className="w-6 h-6" /> : <KeyRound className="w-6 h-6" />}
        </div>
        <div className="mt-5 text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">
          Two-Factor Authentication
        </div>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-900 mt-2">
          {setupRequired ? "Hubungkan 2FAS Auth" : "Masukkan kode authenticator"}
        </h1>

        {setupRequired ? (
          <div className="mt-3">
            <p className="text-sm text-zinc-500 leading-relaxed">
              Buka aplikasi 2FAS Auth, pilih tambah akun, lalu scan QR Code berikut. Setelah terhubung,
              masukkan kode 6 digit yang muncul di aplikasi.
            </p>

            <div className="mt-6 border border-zinc-200 bg-zinc-50 p-5 flex min-h-64 items-center justify-center">
              {loadingSetup && <div className="text-sm text-zinc-500">Memuat QR Code...</div>}
              {!loadingSetup && qrCodeDataUri && (
                <img
                  data-testid="totp-qr-code"
                  src={qrCodeDataUri}
                  alt="QR Code untuk menghubungkan akun dengan aplikasi authenticator"
                  className="w-56 h-56 bg-white"
                />
              )}
            </div>

            {manualSetupKey && (
              <div className="mt-3 border border-zinc-200 px-4 py-3">
                <div className="text-[10px] uppercase tracking-widest font-semibold text-zinc-500">
                  Tidak bisa scan? Masukkan kunci manual
                </div>
                <div className="mt-2 flex items-center justify-between gap-3">
                  <code className="text-sm font-bold tracking-wider text-zinc-900 break-all">
                    {manualSetupKey.match(/.{1,4}/g)?.join(" ")}
                  </code>
                  <button
                    type="button"
                    onClick={copyManualKey}
                    className="shrink-0 p-2 text-[#002FA7] hover:bg-[#002FA7]/10"
                    aria-label="Salin kunci setup"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="mt-3 flex gap-3 border border-zinc-200 bg-zinc-50 p-4">
            <Smartphone className="w-5 h-5 text-[#002FA7] shrink-0" />
            <p className="text-sm text-zinc-600 leading-relaxed">
              Buka aplikasi 2FAS Auth dan masukkan kode 6 digit untuk akun HRIS Anda.
            </p>
          </div>
        )}

        <form onSubmit={onVerify} className="mt-7">
          <label htmlFor="totp-code" className="block text-xs font-semibold text-zinc-900 uppercase tracking-wider mb-2">
            Kode Authenticator
          </label>
          <input
            id="totp-code"
            data-testid="otp-code-input"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]{6}"
            maxLength={6}
            autoFocus={!setupRequired}
            value={code}
            onChange={(event) => {
              setCode(event.target.value.replace(/\D/g, "").slice(0, 6));
              setError("");
            }}
            className="w-full border border-zinc-300 px-4 py-3 text-center font-mono text-3xl tracking-[0.45em] text-zinc-900 focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] focus:outline-none"
            aria-describedby={error ? "totp-error" : "totp-help"}
          />

          <div id="totp-help" className={`mt-3 text-xs ${challengeExpired ? "text-red-600" : "text-zinc-500"}`}>
            {challengeExpired
              ? "Sesi verifikasi telah kedaluwarsa. Kembali ke login untuk memulai lagi."
              : "Kode di aplikasi berubah otomatis setiap 30 detik."}
          </div>
          {error && (
            <div id="totp-error" role="alert" className="mt-3 border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}

          <button
            data-testid="otp-verify-button"
            type="submit"
            disabled={verifying || loadingSetup || code.length !== 6 || challengeExpired}
            className="mt-5 w-full bg-[#002FA7] text-white px-5 py-3 font-semibold hover:bg-[#002FA7]/90 focus:ring-2 focus:ring-[#002FA7]/30 focus:outline-none disabled:opacity-50"
          >
            {verifying ? "Memverifikasi..." : (setupRequired ? "Aktifkan Authenticator" : "Verifikasi dan Masuk")}
          </button>
        </form>
      </div>
    </div>
  );
}
